/** Words for the window slot (SA-1136). `rangeWords` is the twin of `windows.range_words`. */
import type { Flexibility, WindowSlotValue } from './window'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

/** A real calendar day in ISO form, as `date.fromisoformat` reads one (`2026-02-30`, `2026-13-01`
 * and year 0 are not). SA-1138: the twin's guard against a malformed server value. */
export function isIsoDay(s: unknown): s is string {
  if (typeof s !== 'string') return false
  const m = ISO.exec(s)
  if (!m) return false
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  if (y < 1 || mo < 1 || mo > 12 || d < 1) return false
  return d <= new Date(Date.UTC(2000 + (y % 400), mo, 0)).getUTCDate()
}

function parts(iso: string): { y: number; m: number; d: number } | null {
  if (!isIsoDay(iso)) return null
  const m = ISO.exec(iso) as RegExpExecArray
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) }
}
const full = (p: { y: number; m: number; d: number }) => `${p.d} ${MONTHS[p.m - 1]} ${p.y}`

export function rangeWords(earliest: string | null, latest: string | null): string {
  const a = earliest ? parts(earliest) : null
  const b = latest ? parts(latest) : null
  if ((earliest && !a) || (latest && !b)) return ''   // SA-1138: a date the calendar lacks prints nothing
  if (a && b) {
    if (earliest === latest) return full(a)
    if (a.y === b.y && a.m === b.m) return `${a.d} to ${full(b)}`
    if (a.y === b.y) return `${a.d} ${MONTHS[a.m - 1]} to ${full(b)}`
    return `${full(a)} to ${full(b)}`
  }
  if (a) return `from ${full(a)}`
  if (b) return `by ${full(b)}`
  return ''
}

export const FLEXIBILITY_WORDS: Record<Flexibility, string> = {
  fixed: 'fixed dates',
  flexible: 'flexible',
  asap: 'as soon as possible',
  unknown: 'not stated',
}

export const AS_WRITTEN = 'As the customer wrote it'
export const WORDS_PLACEHOLDER = 'e.g. mid-October, flexible'
export const EARLIEST = 'Earliest'
export const LATEST = 'Latest'
export const FLEXIBILITY = 'Flexibility'
export const GUESSED = 'Dates guessed from the text'
/** The register's chip for a guessed window (SA-1141); `GUESSED` is its title. */
export const GUESSED_SHORT = 'guessed'
export const CONFIRM = 'Confirm dates'

/** One line for a cell or a value: the range with the flexibility, else the text. */
export function windowLine(v: WindowSlotValue): string {
  const range = rangeWords(v.earliest, v.latest)
  if (!range) return v.text
  return v.flexibility === 'unknown' ? range : `${range}, ${FLEXIBILITY_WORDS[v.flexibility]}`
}
